import { Controller } from "@hotwired/stimulus"
import intlTelInput from "intl-tel-input"
import { forbiddenNanp911E164 } from "../utils/phone_number_rules"

// intl-tel-input for account/admin phone fields; syncs E.164 into a hidden field (same library as contact form).
export default class extends Controller {
  static targets = ["phoneIti", "phoneHidden", "phoneError"]

  static values = {
    excludedCountries: { type: Array, default: [] },
  }

  connect() {
    this.iti = null
    this.boundIntlSync = null
    this.form = this.element.closest("form")
    this.boundPrepareSubmit = (event) => this.prepareForFormSubmit(event)
    if (this.form) this.form.addEventListener("submit", this.boundPrepareSubmit)
    this.initIntlPhone()
  }

  disconnect() {
    if (this.form && this.boundPrepareSubmit) {
      this.form.removeEventListener("submit", this.boundPrepareSubmit)
    }
    this.form = null
    if (this.iti && this.boundIntlSync) {
      this.phoneItiTarget.removeEventListener("countrychange", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("input", this.boundIntlSync)
      this.phoneItiTarget.removeEventListener("blur", this.boundIntlSync)
      this.iti.destroy()
      this.iti = null
    }
  }

  prepareForFormSubmit(event) {
    this.syncIntlPhoneToHidden()
    const state = this.phoneFieldState()
    this.applyPhoneErrorFeedback(state.error)
    if (!state.valid && this.phoneItiTarget.value.trim() !== "") {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  initIntlPhone() {
    this.boundIntlSync = () => {
      this.syncIntlPhoneToHidden()
      this.applyPhoneErrorFeedback(this.phoneFieldState().error)
    }

    this.iti = intlTelInput(this.phoneItiTarget, {
      initialCountry: "us",
      countryOrder: ["us"],
      excludeCountries: this.excludedCountriesValue,
      loadUtils: () => import("intl-tel-input/utils"),
      customPlaceholder: (nationalExample, countryData) => {
        const prefix = countryData?.dialCodePlus
        if (!prefix) return nationalExample || ""
        if (!nationalExample) return prefix
        return `${prefix} ${nationalExample}`
      },
    })

    this.phoneItiTarget.addEventListener("countrychange", this.boundIntlSync)
    this.phoneItiTarget.addEventListener("input", this.boundIntlSync)
    this.phoneItiTarget.addEventListener("blur", this.boundIntlSync)

    this.iti.promise.then(() => {
      this.restoreIntlFromHidden()
      this.applyPhoneErrorFeedback(this.phoneFieldState().error)
    })
  }

  restoreIntlFromHidden() {
    const v = this.phoneHiddenTarget.value.trim()
    if (!v || !this.iti) return
    this.iti.setNumber(v)
  }

  syncIntlPhoneToHidden() {
    if (!this.iti) return
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") {
      this.phoneHiddenTarget.value = ""
      return
    }
    if (!intlTelInput.utils) return
    const e164 = this.iti.getNumber()
    this.phoneHiddenTarget.value = e164 || ""
  }

  phoneFieldState() {
    const trimmed = this.phoneItiTarget.value.trim()
    if (trimmed === "") return { valid: true, error: "" }
    if (!this.iti || !intlTelInput.utils) return { valid: false, error: "" }

    this.syncIntlPhoneToHidden()
    const e164 = this.phoneHiddenTarget.value.trim()
    if (forbiddenNanp911E164(e164)) {
      return {
        valid: false,
        error: "911 cannot be used in the area code or as the first three digits of the local number.",
      }
    }

    if (this.iti.isValidNumber() === true) return { valid: true, error: "" }
    return { valid: false, error: "Enter a valid phone number for the selected country." }
  }

  applyPhoneErrorFeedback(message) {
    if (!this.hasPhoneErrorTarget) return

    const show = message !== ""
    this.phoneErrorTarget.textContent = message
    this.phoneErrorTarget.classList.toggle("hidden", !show)

    const errId = this.phoneErrorTarget.id
    if (show && errId) {
      this.phoneItiTarget.setAttribute("aria-invalid", "true")
      this.phoneItiTarget.setAttribute("aria-errormessage", errId)
    } else {
      this.phoneItiTarget.removeAttribute("aria-invalid")
      this.phoneItiTarget.removeAttribute("aria-errormessage")
    }
  }
}
